import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navigation } from '../components/Navigation';
import { useProgress } from '../hooks/useProgress';
import { useAuth } from '../hooks/useAuth';
import { CheckCircle, Rocket, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const { PREMIUM_ORBITS, setPremium } = useProgress();
  const { user } = useAuth();
  const [status, setStatus] = useState('verifying');

  useEffect(() => {
    let attempts = 0;
    const maxAttempts = 8;

    const verify = async () => {
      try {
        // Try the verify-payment endpoint
        const res = await fetch(`${API_URL}/api/premium/verify-payment`, {
          method: 'POST',
          credentials: 'include',
        });
        if (res.ok) {
          const data = await res.json();
          if (data.is_premium) {
            setPremium(true);
            setStatus('success');
            return;
          }
        }
      } catch { /* ignore */ }

      attempts++;
      if (attempts < maxAttempts) {
        setTimeout(verify, 3000);
      } else {
        // Even if verification fails, show success page (webhook may process later)
        setPremium(true);
        setStatus('success');
      }
    };

    if (user) {
      verify();
    } else {
      // Not logged in - mark success (they can verify later)
      setPremium(true);
      setStatus('success');
    }
  }, [user, setPremium]);

  return (
    <div style={{ minHeight: '100vh', background: '#030305' }}>
      <Navigation />
      <div className="grid-bg" style={{ paddingTop: '100px', paddingBottom: '60px', minHeight: 'calc(100vh - 64px)' }}>
        <div style={{ maxWidth: '600px', margin: '0 auto', padding: '0 24px', textAlign: 'center' }}>
          <div className="animate-fadeIn">
            {status === 'verifying' ? (
              <>
                <div style={{ width: '100px', height: '100px', borderRadius: '50%', background: 'rgba(255,215,0,0.1)', border: '2px solid rgba(255,215,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
                  <Loader2 size={50} color="#FFD700" className="animate-spin" />
                </div>
                <h1 style={{ fontFamily: 'Rajdhani', fontSize: '36px', fontWeight: 700, color: '#FFD700', marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                  Verifying Payment...
                </h1>
                <p style={{ fontFamily: 'Outfit', fontSize: '18px', color: 'rgba(255,255,255,0.6)', lineHeight: 1.7 }}>
                  Please wait while we confirm your payment.
                </p>
              </>
            ) : (
              <>
                <div style={{ width: '100px', height: '100px', borderRadius: '50%', background: 'rgba(0,255,148,0.1)', border: '2px solid #00FF94', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
                  <CheckCircle size={50} color="#00FF94" />
                </div>
                <h1 style={{ fontFamily: 'Rajdhani', fontSize: '36px', fontWeight: 700, color: '#00FF94', marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                  Welcome to Premium!
                </h1>
                <p style={{ fontFamily: 'Outfit', fontSize: '18px', color: 'rgba(255,255,255,0.8)', marginBottom: '32px', lineHeight: 1.7 }}>
                  You now have lifetime access to all advanced orbits and premium features.
                </p>

                <div className="mb-8 p-6 rounded-xl" style={{ background: 'rgba(0,255,148,0.05)', border: '1px solid rgba(0,255,148,0.2)' }}>
                  <p style={{ fontFamily: 'JetBrains Mono', fontSize: '11px', color: '#00FF94', letterSpacing: '0.15em', marginBottom: '16px' }}>UNLOCKED CONTENT</p>
                  <div className="flex flex-wrap justify-center gap-2">
                    {PREMIUM_ORBITS.map(id => (
                      <span key={id} style={{ padding: '6px 14px', borderRadius: '9999px', background: 'rgba(0,255,148,0.1)', border: '1px solid rgba(0,255,148,0.3)', fontFamily: 'Rajdhani', fontWeight: 600, fontSize: '13px', color: '#00FF94', textTransform: 'uppercase' }}>
                        {id.replace('_', ' ')}
                      </span>
                    ))}
                  </div>
                  <div className="flex flex-wrap justify-center gap-2 mt-3">
                    <span style={{ padding: '6px 14px', borderRadius: '9999px', background: 'rgba(232,121,249,0.1)', border: '1px solid rgba(232,121,249,0.3)', fontFamily: 'Rajdhani', fontWeight: 600, fontSize: '13px', color: '#E879F9', textTransform: 'uppercase' }}>
                      TLE Parser
                    </span>
                    <span style={{ padding: '6px 14px', borderRadius: '9999px', background: 'rgba(232,121,249,0.1)', border: '1px solid rgba(232,121,249,0.3)', fontFamily: 'Rajdhani', fontWeight: 600, fontSize: '13px', color: '#E879F9', textTransform: 'uppercase' }}>
                      Camera Lock
                    </span>
                  </div>
                </div>

                <button
                  onClick={() => navigate('/learn')}
                  data-testid="start-learning-btn"
                  className="px-8 py-4 rounded-xl flex items-center gap-3 mx-auto"
                  style={{ fontFamily: 'Rajdhani', fontWeight: 700, fontSize: '16px', textTransform: 'uppercase', letterSpacing: '0.1em', background: 'linear-gradient(135deg, #00FF94, #00F0FF)', color: '#000', border: 'none', cursor: 'pointer', transition: 'transform 0.2s' }}
                >
                  <Rocket size={20} /> Start Exploring
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
